import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import api from "../api/client";
import { useCustomer } from "../context/CustomerContext";
import { usePublishScreenData } from "../utils/aiScreenContext";

/**
 * Cloud compute — one screen for Azure VMs, AWS EC2 and GCP Compute Engine.
 *
 * ONE SCREEN, NOT THREE. The three clouds name the same things differently
 * (VM / instance / instance, managed disk / EBS volume / persistent disk) and
 * their power verbs differ, but an engineer's question is the same in all
 * three: what is running, and can I stop it or snapshot its disk. The
 * per-platform vocabulary is mapped here and the rows are rendered uniformly.
 *
 * WHICH BUTTONS EXIST IS DECIDED BY THE BACKEND. The capability list comes
 * from /supported-platforms, which derives it from each adapter - so a
 * platform whose adapter cannot snapshot simply has no snapshot button,
 * rather than one that fails when pressed.
 *
 * Every action here CHANGES a customer's cloud, so each is confirmed, and the
 * server audits it.
 */

type Cap = { key: string; label: string; writes: boolean; detail: string };
type Platform = {
  platform: string;
  label: string;
  can_write: boolean;
  groups: { key: string; label: string; capabilities: Cap[] }[];
};
type Connector = {
  id: number;
  platform: string;
  platform_label: string;
  name: string;
  enabled: boolean;
  customer_id: number;
};

// Per-platform wiring: which endpoint lists instances, which lists disks, and
// what the power verbs are called. Keyed so adding a platform is a data edit.
const WIRING: Record<
  string,
  {
    instances: string;
    disks?: string;
    // (connectorId, row) -> path for a power action
    power?: (row: any, action: string) => string;
    powerActions: string[];
    snapshot?: (row: any) => string;
    instanceName: (row: any) => string;
    instanceMeta: (row: any) => string;
    instanceState: (row: any) => string | null;
    /** Where it lives, in that platform's words (region/zone/resource group). */
    instanceWhere: (row: any) => string;
    /** Addresses, private first. Empty when the platform did not return any. */
    instanceIps: (row: any) => string[];
    /** The size/type NAME. The vCPU and RAM behind it are normalised by the
     *  server onto vcpus/memory_gb, so they need no per-platform accessor. */
    instanceSize: (row: any) => string | null;
    diskName: (row: any) => string;
    diskMeta: (row: any) => string;
  }
> = {
  azure: {
    instances: "vms",
    disks: "disks",
    power: (r, a) => `vms/${encodeURIComponent(r.resource_group)}/${encodeURIComponent(r.name)}/${a}`,
    // deallocate is the one that actually stops Azure billing, which is why
    // it is offered alongside stop rather than hidden behind it.
    powerActions: ["start", "stop", "deallocate", "restart"],
    snapshot: (r) =>
      `disks/${encodeURIComponent(r.resource_group)}/${encodeURIComponent(r.name)}/snapshot`,
    instanceName: (r) => r.name,
    instanceMeta: (r) => [r.vm_size, r.location, r.resource_group].filter(Boolean).join(" · "),
    instanceState: (r) => r.power_state,
    instanceWhere: (r) => [r.location, r.resource_group].filter(Boolean).join(" · "),
    instanceIps: (r) => [r.private_ip, r.public_ip].filter(Boolean),
    instanceSize: (r) => r.vm_size || null,
    diskName: (r) => r.name,
    diskMeta: (r) =>
      [r.size_gb ? `${r.size_gb} GB` : null, r.sku, r.state, r.attached_to && `on ${r.attached_to}`]
        .filter(Boolean)
        .join(" · "),
  },
  aws: {
    instances: "ec2-instances",
    disks: "ebs-volumes",
    power: (r, a) => `ec2/${encodeURIComponent(r.instance_id || r.id)}/${a}`,
    powerActions: ["start", "stop", "reboot"],
    snapshot: (r) => `ebs/${encodeURIComponent(r.volume_id || r.id)}/snapshot`,
    instanceName: (r) => r.name || r.instance_id || r.id,
    instanceMeta: (r) =>
      [r.instance_type, r.availability_zone || r.region, r.private_ip, r.public_ip]
        .filter(Boolean)
        .join(" · "),
    instanceState: (r) => r.state,
    instanceWhere: (r) => r.availability_zone || r.az || r.region || "",
    instanceIps: (r) => [r.private_ip, r.public_ip].filter(Boolean),
    instanceSize: (r) => r.instance_type || null,
    diskName: (r) => r.name || r.volume_id || r.id,
    diskMeta: (r) =>
      [r.size_gb ? `${r.size_gb} GB` : null, r.volume_type, r.state, r.attached_to]
        .filter(Boolean)
        .join(" · "),
  },
  gcp: {
    instances: "gce-instances",
    disks: "gce-disks",
    power: (r, a) => `gce/${encodeURIComponent(r.zone)}/${encodeURIComponent(r.name)}/${a}`,
    // GCP calls a hard restart "reset".
    powerActions: ["start", "stop", "reset"],
    snapshot: (r) =>
      `gce-disks/${encodeURIComponent(r.zone)}/${encodeURIComponent(r.name)}/snapshot`,
    instanceName: (r) => r.name,
    instanceMeta: (r) =>
      [r.machine_type, r.zone, r.internal_ip, r.external_ip].filter(Boolean).join(" · "),
    instanceState: (r) => r.status,
    instanceWhere: (r) => r.zone || "",
    instanceIps: (r) => [r.internal_ip, r.external_ip].filter(Boolean),
    instanceSize: (r) => r.machine_type || null,
    diskName: (r) => r.name,
    diskMeta: (r) =>
      [r.size_gb ? `${r.size_gb} GB` : null, r.type, r.zone, r.attached_to && `on ${r.attached_to}`]
        .filter(Boolean)
        .join(" · "),
  },
};

/* The hardware, in words an engineer can act on.
 *
 * REPORTED: "I cannot even tell the spec on the machines. Add more data."
 * The row used to print the size NAME only - "Standard_D2s_v3", "t3.medium",
 * "e2-medium" - which is only meaningful if you have the vendor's catalogue
 * memorised. The server now resolves each size to cores and memory (one
 * lookup per size, not per machine), and these read whatever came back.
 *
 * Every field is optional on purpose: an older appliance, a platform whose
 * sizes API refused, or a custom shape all leave gaps, and a row that shows
 * what it knows beats a row that shows nothing.
 */
const specOf = (r: any): string | null => {
  const bits = [
    r.vcpus ? `${r.vcpus} vCPU` : null,
    r.memory_gb ? `${r.memory_gb} GB RAM` : null,
  ].filter(Boolean);
  return bits.length ? bits.join(" · ") : null;
};

const osOf = (r: any): string | null => r.os_image || r.os_type || null;

const storageOf = (r: any): string | null => {
  const bits = [
    r.os_disk_gb ? `${r.os_disk_gb} GB boot disk` : null,
    r.data_disks ? `${r.data_disks} data disk${r.data_disks === 1 ? "" : "s"}` : null,
  ].filter(Boolean);
  return bits.length ? bits.join(" · ") : null;
};

/** One labelled fact. Kept small and quiet so the row stays scannable. */
function Fact({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
        {label}
      </div>
      <div className="text-xs text-gray-700 dark:text-gray-300 truncate">{value}</div>
    </div>
  );
}

const stateTone = (s: string | null) => {
  const v = (s || "").toLowerCase();
  if (v.includes("run")) return "text-emerald-600 dark:text-emerald-400";
  if (v.includes("stop") || v.includes("dealloc") || v.includes("terminat"))
    return "text-gray-500 dark:text-gray-400";
  return "text-amber-600 dark:text-amber-400";
};

export default function CloudCompute() {
  const { currentCustomerId } = useCustomer();
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [selected, setSelected] = useState<number | "">("");
  const [instances, setInstances] = useState<any[]>([]);
  const [disks, setDisks] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      api.get("/api/v1/cloud-connectors/supported-platforms"),
      api.get("/api/v1/cloud-connectors"),
    ])
      .then(([p, c]) => {
        setPlatforms(p.data || []);
        setConnectors(c.data || []);
      })
      .catch(() => setErr("Could not load cloud connectors."));
  }, []);

  // Only connectors whose platform actually has a compute group.
  const usable = useMemo(() => {
    const withCompute = new Set(
      platforms
        .filter((p) => p.groups.some((g) => g.key === "compute"))
        .map((p) => p.platform),
    );
    return connectors.filter(
      (c) =>
        c.enabled &&
        withCompute.has(c.platform) &&
        (!currentCustomerId || c.customer_id === currentCustomerId),
    );
  }, [platforms, connectors, currentCustomerId]);

  const current = usable.find((c) => c.id === selected);
  const wiring = current ? WIRING[current.platform] : undefined;
  const caps = useMemo(() => {
    const p = platforms.find((x) => x.platform === current?.platform);
    const g = p?.groups.find((x) => x.key === "compute");
    return new Set((g?.capabilities || []).map((c) => c.key));
  }, [platforms, current]);

  const canSnapshot = ["disk_snapshot", "ebs_snapshot", "gce_disk_snapshot"].some((k) =>
    caps.has(k),
  );
  const canPower = ["vm_power", "ec2_power", "gce_power"].some((k) => caps.has(k));

  /* Hand the visible fleet to the AI Assist.
   *
   * REPORTED: "I think the AI assist may need updating under cloud, especially
   * with compute... Networking isn't showing me much either." It was not that
   * the assistant was weak here - it was that this screen published NOTHING,
   * so the model was answering about cloud compute in the abstract while a
   * list of real instances sat in front of the user.
   *
   * NORMALISED, and deliberately so. Each platform names the same idea
   * differently (vm_size / instance_type / machine_type, power_state / state /
   * status), and shipping those raw would both confuse the model and put
   * vendor identifiers into AI output, which the product does not do. WIRING
   * already holds the accessors the table renders with, so this reuses them
   * rather than growing a second mapping that could disagree with the screen.
   */
  const aiItems = useMemo(() => {
    if (!wiring) return [];
    return [
      ...instances.map((r) => ({
        kind: "instance",
        name: wiring.instanceName(r),
        state: wiring.instanceState(r),
        size: wiring.instanceSize(r),
        vcpus: r.vcpus ?? null,
        memory_gb: r.memory_gb ?? null,
        os: osOf(r),
        storage: storageOf(r),
        location: wiring.instanceWhere(r),
        addresses: wiring.instanceIps(r),
      })),
      ...disks.map((r) => ({
        kind: "disk",
        name: wiring.diskName(r),
        details: wiring.diskMeta(r),
      })),
    ];
  }, [wiring, instances, disks]);

  usePublishScreenData(
    "Cloud compute",
    aiItems,
    current
      ? `${instances.length} instance(s) and ${disks.length} disk(s) on the "${current.name}" ` +
        `connection. Power controls ${canPower ? "are" : "are not"} available here; ` +
        `snapshots ${canSnapshot ? "are" : "are not"}.`
      : "No cloud connection selected yet.",
  );

  const load = useCallback(async () => {
    if (!current || !wiring) return;
    setLoading(true);
    setErr("");
    try {
      const base = `/api/v1/cloud-connectors/${current.id}`;
      const inst = await api.get(`${base}/${wiring.instances}`);
      setInstances(inst.data || []);
      if (wiring.disks) {
        try {
          const d = await api.get(`${base}/${wiring.disks}`);
          setDisks(d.data || []);
        } catch {
          setDisks([]);
        }
      } else {
        setDisks([]);
      }
    } catch (e: any) {
      setErr(e?.response?.data?.detail || "Could not read that cloud.");
      setInstances([]);
      setDisks([]);
    } finally {
      setLoading(false);
    }
  }, [current, wiring]);

  useEffect(() => {
    if (selected) load();
  }, [selected, load]);

  const act = async (row: any, action: string) => {
    if (!current || !wiring?.power) return;
    const name = wiring.instanceName(row);
    if (
      !window.confirm(
        `${action.toUpperCase()} "${name}" in ${current.platform_label}?\n\n` +
          `This changes the customer's cloud and is recorded in the audit log.`,
      )
    )
      return;
    setBusy(`${name}-${action}`);
    setErr("");
    try {
      await api.post(`/api/v1/cloud-connectors/${current.id}/${wiring.power(row, action)}`);
      setMsg(`${action} sent to ${name}.`);
      setTimeout(() => setMsg(""), 4000);
      await load();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || `Could not ${action} ${name}.`);
    } finally {
      setBusy(null);
    }
  };

  const snapshot = async (row: any) => {
    if (!current || !wiring?.snapshot) return;
    const name = wiring.diskName(row);
    if (!window.confirm(`Snapshot "${name}"? This creates a new snapshot and may incur cost.`))
      return;
    setBusy(`snap-${name}`);
    setErr("");
    try {
      const r = await api.post(`/api/v1/cloud-connectors/${current.id}/${wiring.snapshot(row)}`);
      setMsg(r.data?.snapshot_name ? `Snapshot ${r.data.snapshot_name} created.` : "Snapshot created.");
      setTimeout(() => setMsg(""), 5000);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || `Could not snapshot ${name}.`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Cloud compute</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Virtual machines and their disks across Azure, AWS and Google Cloud. Power actions and
          snapshots change the customer's cloud and are audited.
        </p>
      </div>

      {err && (
        <div className="mb-4 bg-red-50 dark:bg-red-900/40 text-red-700 dark:text-red-300 text-sm px-4 py-2 rounded-lg">
          {err}
        </div>
      )}
      {msg && (
        <div className="mb-4 bg-green-50 dark:bg-green-900/40 text-green-700 dark:text-green-300 text-sm px-4 py-2 rounded-lg">
          {msg}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <select
          value={selected}
          onChange={(e) => setSelected(e.target.value ? Number(e.target.value) : "")}
          className="border dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
        >
          <option value="">Choose a connector…</option>
          {usable.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name} — {c.platform_label}
            </option>
          ))}
        </select>
        {selected && (
          <button
            onClick={load}
            disabled={loading}
            className="text-xs border dark:border-gray-600 dark:text-gray-200 rounded-lg px-2.5 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700/40 disabled:opacity-50"
          >
            {loading ? "Loading…" : "Refresh"}
          </button>
        )}
      </div>

      {usable.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-6 text-sm text-gray-600 dark:text-gray-300">
          No cloud connector with compute is set up
          {currentCustomerId ? " for this customer" : ""}.{" "}
          <Link to="/cloud" className="text-blue-600 dark:text-blue-400 hover:underline">
            Add one
          </Link>
          .
        </div>
      )}

      {current && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 text-sm font-semibold text-gray-700 dark:text-gray-300">
              Instances{" "}
              <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                ({instances.length})
              </span>
            </div>
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {instances.map((row, i) => (
                <div
                  key={i}
                  className="px-4 py-3 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between"
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      {wiring!.instanceName(row)}{" "}
                      <span className={`text-xs ${stateTone(wiring!.instanceState(row))}`}>
                        {wiring!.instanceState(row) || "unknown"}
                      </span>
                    </div>
                    {/* The spec, spelled out. Grid rather than a joined string
                        so the columns line up down the list and the eye can
                        compare two machines without reading both sentences. */}
                    <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-4 gap-y-2">
                      <Fact
                        label="Size"
                        value={
                          <>
                            {specOf(row) || "—"}
                            {wiring!.instanceSize(row) && (
                              <span className="text-gray-400 dark:text-gray-500">
                                {" "}
                                ({wiring!.instanceSize(row)})
                              </span>
                            )}
                          </>
                        }
                      />
                      <Fact label="Operating system" value={osOf(row) || "—"} />
                      <Fact label="Storage" value={storageOf(row) || "—"} />
                      <Fact label="Location" value={wiring!.instanceWhere(row) || "—"} />
                      <Fact
                        label="Addresses"
                        value={
                          wiring!.instanceIps(row).length ? (
                            <span className="font-mono">
                              {wiring!.instanceIps(row).join(", ")}
                            </span>
                          ) : (
                            "—"
                          )
                        }
                      />
                    </div>
                  </div>
                  {canPower && (
                    <div className="flex flex-wrap gap-1.5 shrink-0">
                      {wiring!.powerActions.map((a) => (
                        <button
                          key={a}
                          onClick={() => act(row, a)}
                          disabled={busy === `${wiring!.instanceName(row)}-${a}`}
                          className="text-xs border dark:border-gray-600 dark:text-gray-200 rounded-lg px-2 py-1 hover:bg-gray-50 dark:hover:bg-gray-700/40 disabled:opacity-50"
                        >
                          {busy === `${wiring!.instanceName(row)}-${a}` ? "…" : a}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {!loading && instances.length === 0 && (
                <div className="px-4 py-6 text-center text-xs italic text-gray-500 dark:text-gray-400">
                  No instances found in this {current.platform_label} account.
                </div>
              )}
            </div>
          </div>

          {wiring!.disks && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
              <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 text-sm font-semibold text-gray-700 dark:text-gray-300">
                Disks{" "}
                <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                  ({disks.length})
                </span>
              </div>
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {disks.map((row, i) => (
                  <div
                    key={i}
                    className="px-4 py-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="min-w-0">
                      <div className="font-medium text-gray-900 dark:text-gray-100">
                        {wiring!.diskName(row)}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {wiring!.diskMeta(row)}
                      </div>
                    </div>
                    {canSnapshot && (
                      <button
                        onClick={() => snapshot(row)}
                        disabled={busy === `snap-${wiring!.diskName(row)}`}
                        className="shrink-0 text-xs border dark:border-gray-600 dark:text-gray-200 rounded-lg px-2 py-1 hover:bg-gray-50 dark:hover:bg-gray-700/40 disabled:opacity-50"
                      >
                        {busy === `snap-${wiring!.diskName(row)}` ? "…" : "Snapshot"}
                      </button>
                    )}
                  </div>
                ))}
                {!loading && disks.length === 0 && (
                  <div className="px-4 py-6 text-center text-xs italic text-gray-500 dark:text-gray-400">
                    No disks reported.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
